/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import SummaryIndicator from './SummaryIndicator'
import SieauEChart from 'components/echart/SieauEChart'
import { linkStationDetailsShape } from 'territory/shapes/A3PShapes'
import { NoDataPanel } from 'components/card/NoDataCard'
import { yAutomaticScaleValues } from 'components/echart/EChartUtils'
import { isUndefined, includes, clamp, min as lodashMin, round, padStart, isNil, fromPairs, sortBy, uniq, intersection, isEmpty, values, keys } from 'lodash'
import { Grid } from '@mui/material'
import moment from 'moment'
import { DEPTH } from 'utils/PiezometryUtils'
import { useSelector } from 'react-redux'
import { HYDRO_MEASURE_COTE, HYDROMETER_HEIGHT_TYPE } from 'hydrometry/constants/HydrometryConstants'
import { ChipLabel, getCote, getHydroCote } from 'components/echart/ChartOptionsChip'
import { MEASURE_COTE } from 'piezometry/constants/PiezometryConstants'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { hasValue } from 'utils/NumberUtil'
import { frequencyColors } from 'piezometry/components/suivi/utils/SuiviStatsUtils'
import { blackColor } from 'utils/constants/ColorTheme'

const DATATYPES_WITH_NGF = [DEPTH, HYDROMETER_HEIGHT_TYPE]

const DEFAULT_VALUE = 1
const NGF_VALUE = 2

const ENVELOPES = [
    { key: 'dryTriennial', label: 'fre_3' },
    { key: 'dryQuadrennial', label: 'fre_4' },
    { key: 'dryQuinquennial', label: 'fre_5' },
]

const CONFIDENCE_LEVELS = [
    { level: 90, min: 'min90Measures', max: 'max90Measures' },
    { level: 70, min: 'min70Measures', max: 'max70Measures' },
    { level: 50, min: 'min50Measures', max: 'max50Measures' },
]

const pctToHexAlpha = pct => padStart(round(clamp(pct, 0, 100)))

const chartHeight = 500

const FollowAndPredictionResourceChart = ({ selectedStation }) => {
    const {
        hydrometryDataTypes,
        hydrometryThresholds,
        piezometryDataTypes,
        allPiezometerThresholds,
    } = useSelector(store => ({
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        hydrometryThresholds: store.HydrometryReducer.hydrometryThresholds,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        allPiezometerThresholds: store.PiezometerStationReducer.allPiezometerThresholds,
    }))

    const isPiezo = useMemo(() => selectedStation?.dataType === DEPTH, [selectedStation])
    const dataTypes = useMemo(() => isPiezo ? piezometryDataTypes : hydrometryDataTypes, [isPiezo, piezometryDataTypes, hydrometryDataTypes])
    const dataType = useMemo(() => dataTypes.find(({ id }) => id === selectedStation?.dataType), [dataTypes, selectedStation])
    const thresholds = useMemo(() => (isPiezo ? allPiezometerThresholds : hydrometryThresholds).filter(threshold => (
        parseInt(threshold.dataType) === dataType?.id && !isEmpty(intersection([threshold.code, parseInt(threshold.stationId)], [selectedStation?.idStation, selectedStation?.code]))
    )), [allPiezometerThresholds, hydrometryThresholds, isPiezo, selectedStation, dataType])

    const [displayCote, setDisplayCote] = useState(isPiezo ? MEASURE_COTE.DEPTH : HYDRO_MEASURE_COTE.HEIGHT)
    const displayNGF = useMemo(() => displayCote === HYDRO_MEASURE_COTE.NGF && includes(DATATYPES_WITH_NGF, selectedStation?.dataType), [displayCote, selectedStation])
    const [selectedLegend, setSelectedLegend] = useState({})

    const ZONE_COLORS = frequencyColors(4, true)

    useEffect(() => {
        setDisplayCote(isPiezo ? MEASURE_COTE.DEPTH : HYDRO_MEASURE_COTE.HEIGHT)
    }, [selectedStation, isPiezo])

    const getContent = () => {
        if (isUndefined(selectedStation)) return <NoDataPanel />
        const grid = [{
            id: 'followAndPredictionResourceChart',
            right: 100,
            width: '85%',
            top: 50,
            bottom: 40,
        }]

        const volumeValues = selectedStation.measures ?? []
        const previVolumeValues = selectedStation.previMeasures ?? []
        const unit = dataType?.unit ?? ''

        const valueIndex = displayNGF ? NGF_VALUE : DEFAULT_VALUE
        const toSeriesData = arr => (arr || []).map(([date, value, ngfValue]) => [date, displayNGF ? ngfValue : value])

        const xAxis = [{
            gridIndex: 0,
            type: 'time',
            position: 'bottom',
            nameGap: 35,
            nameLocation: 'center',
            axisLine: {
                show: true,
                lineStyle: { color: '#000' },
            },
            splitLine: {
                show: true,
            },
            axisLabel: {
                formatter: (value) => moment(value).format('DD/MM/YYYY'),
            },
        }]

        const scaleValues = [
            ...volumeValues,
            ...previVolumeValues,
            ...ENVELOPES.flatMap(({ key }) => selectedStation[key] || []),
            ...CONFIDENCE_LEVELS.flatMap(({ min, max }) => [...(selectedStation[min] || []), ...(selectedStation[max] || [])]),
        ].map(point => point?.[valueIndex]).filter(hasValue)
        if (!scaleValues.length) return <NoDataPanel />

        const dataValues = [...volumeValues, ...previVolumeValues].map(point => point?.[valueIndex]).filter(hasValue)
        const scaleFloor = dataValues.length ? Math.min(lodashMin(dataValues), 0) : lodashMin(scaleValues)
        const { min, max, interval, splitNumber } = yAutomaticScaleValues(scaleValues.map(value => Math.max(value, scaleFloor)))
        const newMin = scaleFloor >= 0 ? clamp(min, 0, max) : min
        const newMax = max + newMin - min

        const yAxis = [{
            gridIndex: 0,
            type: 'value',
            name: dataType ? `${dataType.label}${unit ? ` (${unit})` : ''}` : undefined,
            nameLocation: 'center',
            nameGap: 50,
            min: newMin,
            max: newMax,
            interval,
            splitNumber,
            inverse: isPiezo && !displayNGF,
            axisLine: {
                show: true,
                lineStyle: { color: '#000' },
            },
            axisTick: {
                show: true,
            },
            axisLabel: {
                formatter: value => round(value, 2),
            },
        }]

        const baseSeries = {
            xAxisIndex: 0,
            yAxisIndex: 0,
            type: 'line',
            symbol: 'none',
            silent: true,
            data: [],
        }

        const labelByEnv = {
            fre_5: `< ${i18n.fre_5}`,
            fre_4: `< ${i18n.fre_4}`,
            fre_3: `< ${i18n.fre_3}`,
        }
        const lineColorByEnv = {
            fre_5: ZONE_COLORS[0],
            fre_4: ZONE_COLORS[1],
            fre_3: ZONE_COLORS[2],
        }

        const sortedEnvelopes = ENVELOPES
            .map(env => ({ ...env, points: toSeriesData(selectedStation[env.key]) }))
            .sort((a, b) => (a.points[0]?.[1] ?? 0) - (b.points[0]?.[1] ?? 0))

        const activeEnvelopes = sortedEnvelopes.filter(env => selectedLegend[labelByEnv[env.label]] !== false)
        const capLabel = `> ${i18n.fre_3}`

        // Couleur de REMPLISSAGE par POSITION de sévérité (pas par libellé) : la bande la plus sévère
        // est rouge, la moins sévère bleue. En profondeur l'axe est inversé (le plus profond = le plus
        // sévère est en bas), donc l'ordre des couleurs de remplissage est inversé par rapport au NGF.
        const isDepthAxis = isPiezo && !displayNGF
        const fillColors = isDepthAxis ? [...ZONE_COLORS].reverse() : ZONE_COLORS

        // Bandes empilées (bas -> haut) : chaque courbe borne en haut sa zone ; le dernier élément (null)
        // est le chapeau, qui remplit au-dessus de la courbe la plus haute jusqu'à newMax.
        const dates = sortedEnvelopes[0]?.points ?? []
        const isCapActive = selectedLegend[capLabel] !== false

        const zoneSeries = activeEnvelopes.length ? [...activeEnvelopes, null].map((env, k) => {
            const lowerEnv = k === 0 ? null : activeEnvelopes[k - 1]
            const ownerEnv = isDepthAxis ? lowerEnv : env
            const isCapZone = isNil(ownerEnv)
            const ownerPosition = sortedEnvelopes.indexOf(ownerEnv)
            const capFillColor = isDepthAxis ? fillColors[0] : fillColors[sortedEnvelopes.length]
            const fillColor = isCapZone ? capFillColor : fillColors[isDepthAxis ? ownerPosition + 1 : ownerPosition]
            if (isNil(env)) {
                return {
                    ...baseSeries,
                    z: 1,
                    lineStyle: { opacity: 0 },
                    areaStyle: { origin: 'end', color: fillColor, opacity: isCapZone && !isCapActive ? 0 : 0.25 },
                    tooltip: { show: false },
                    data: lowerEnv.points,
                }
            }
            return {
                ...baseSeries,
                z: 1,
                stack: 'envelopes',
                stackStrategy: 'all',
                lineStyle: { opacity: 0 },
                areaStyle: { color: fillColor, opacity: isCapZone && !isCapActive ? 0 : 0.25, ...(k === 0 ? { origin: 'start' } : {}) },
                tooltip: { show: false },
                data: dates.map(([date], j) => {
                    const lower = isNil(lowerEnv) ? 0 : lowerEnv.points?.[j]?.[1]
                    const upper = env.points?.[j]?.[1]
                    return { value: [date, hasValue(lower) && hasValue(upper) ? upper - lower : null], real: null }
                }),
            }
        }) : []

        const envelopeLineSeries = sortedEnvelopes.map(env => ({
            ...baseSeries,
            name: labelByEnv[env.label],
            color: lineColorByEnv[env.label],
            z: 1,
            lineStyle: { color: lineColorByEnv[env.label], type: 'dashed' },
            data: env.points,
        }))

        const capLegendSeries = {
            ...baseSeries,
            name: capLabel,
            color: ZONE_COLORS[3],
            lineStyle: { opacity: 0, color: ZONE_COLORS[3] },
            areaStyle: { color: ZONE_COLORS[3], opacity: 0.25 },
        }

        const bandSeries = [...zoneSeries, ...envelopeLineSeries, capLegendSeries]

        const confidenceSeries = CONFIDENCE_LEVELS.flatMap(({ level, min: minKey, max: maxKey }, idx) => {
            const minPoints = toSeriesData(selectedStation[minKey])
            const maxPoints = toSeriesData(selectedStation[maxKey])
            if (!minPoints.length || !maxPoints.length) return []
            const name = `Confiance ${level}%`
            const baseConfidenceSeries = { ...baseSeries, name, z: 2, color: `#000000${pctToHexAlpha((idx + 1) * 15)}`, stack: `confidence-${level}`, stackStrategy: 'all', lineStyle: { opacity: 0 } }
            const bounds = minPoints.map(([date, a], j) => {
                const b = maxPoints[j]?.[1]
                const lo = hasValue(a) && hasValue(b) ? Math.min(a, b) : undefined
                const hi = hasValue(a) && hasValue(b) ? Math.max(a, b) : undefined
                return { date, lo, hi }
            })
            return [
                {
                    ...baseConfidenceSeries,
                    areaStyle: { opacity: 0 }, // socle transparent : 0 -> borne basse
                    data: bounds.map(({ date, lo }) => ({ value: [date, lo], real: null })),
                },
                {
                    ...baseConfidenceSeries,
                    areaStyle: { color: blackColor, opacity: 0.15 }, // remplissage : borne basse -> borne haute
                    data: bounds.map(({ date, lo, hi }) => ({
                        value: [date, hasValue(lo) && hasValue(hi) ? hi - lo : null],
                        range: [lo, hi], // intervalle réel pour la tooltip
                    })),
                },
            ]
        })

        const measureByDate = fromPairs(toSeriesData(volumeValues))
        const previByDate = fromPairs(toSeriesData(previVolumeValues))
        const confidencesByDate = CONFIDENCE_LEVELS.map(({ min: minKey, max: maxKey }) => ({
            minByDate: fromPairs(toSeriesData(selectedStation[minKey])),
            maxByDate: fromPairs(toSeriesData(selectedStation[maxKey])),
        }))
        const envelopesByDate = fromPairs(ENVELOPES.map(({ key, label }) => [label, fromPairs(toSeriesData(selectedStation[key]))]))

        const exportDates = sortBy(uniq([
            measureByDate,
            previByDate,
            ...confidencesByDate.flatMap(({ minByDate, maxByDate }) => [minByDate, maxByDate]),
            ...values(envelopesByDate),
        ].flatMap(valuesByDate => keys(valuesByDate).map(Number))))

        const exportHeaders = [i18n.date, i18n.value, i18n.previData, ...CONFIDENCE_LEVELS.flatMap(({ level }) => [`${i18n.trust} ${level}% ${i18n.min}`, `${i18n.trust} ${level}% ${i18n.max}`]), i18n.fre_3_S, i18n.fre_4_S, i18n.fre_5_S]
        const exportRows = exportDates.map(date => ({
            [i18n.date]: moment(date).format('DD/MM/YYYY HH:mm'),
            [i18n.value]: measureByDate[date],
            [i18n.previData]: previByDate[date],
            ...fromPairs(CONFIDENCE_LEVELS.flatMap(({ level }, idx) => {
                const lo = confidencesByDate[idx].minByDate[date]
                const hi = confidencesByDate[idx].maxByDate[date]
                const hasBounds = hasValue(lo) && hasValue(hi)
                return [
                    [`${i18n.trust} ${level}% ${i18n.min}`, hasBounds ? Math.min(lo, hi) : undefined],
                    [`${i18n.trust} ${level}% ${i18n.max}`, hasBounds ? Math.max(lo, hi) : undefined],
                ]
            })),
            [i18n.fre_3_S]: envelopesByDate.fre_3[date],
            [i18n.fre_4_S]: envelopesByDate.fre_4[date],
            [i18n.fre_5_S]: envelopesByDate.fre_5[date],
        }))

        const series = [
            {
                ...baseSeries,
                name: dataType?.label,
                isData: true,
                z: 3,
                data: toSeriesData(volumeValues),
            },
            {
                ...baseSeries,
                name: i18n.previData,
                color: blackColor,
                z: 3,
                lineStyle: { type: 'dashed' },
                data: toSeriesData(previVolumeValues),
            },
            ...confidenceSeries,
            ...bandSeries,
        ]

        return (
            <Grid container size={12} spacing={1}>
                {includes(DATATYPES_WITH_NGF, selectedStation?.dataType) && <ChipLabel
                    label={isPiezo ? getCote(displayCote).label : getHydroCote(displayCote).label}
                    onClick={() => setDisplayCote(prev => {
                        if (isPiezo) return prev === MEASURE_COTE.DEPTH ? MEASURE_COTE.NGF : MEASURE_COTE.DEPTH
                        return prev === HYDRO_MEASURE_COTE.HEIGHT ? HYDRO_MEASURE_COTE.NGF : HYDRO_MEASURE_COTE.HEIGHT
                    })}
                />}
                <Grid size={12}>
                    <SieauEChart
                        key={`${selectedStation.idStation}-${selectedStation.dataType}-${displayCote}`} // réinitialise le zoom vertical au changement de données
                        option={{
                            animation: false,
                            grid,
                            xAxis,
                            yAxis,
                            series,
                            legend: { selected: selectedLegend },
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: {
                                    axis: 'x',
                                    label: {
                                        formatter: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
                                    },
                                },
                                formatter: params => {
                                    if (!params?.length) return ''
                                    const header = moment(params[0].value[0]).format('DD/MM/YYYY HH:mm:ss')
                                    const rows = params
                                        .map(({ marker, seriesName, value, data }) => {
                                            if (data?.range) {
                                                const [lo, hi] = data.range
                                                return hasValue(lo) && hasValue(hi)
                                                    ? { marker, seriesName, text: `${round(lo, 2)} - ${round(hi, 2)} ${unit}` }
                                                    : null
                                            }
                                            const val = data?.real === undefined ? value[1] : data.real
                                            return hasValue(val)
                                                ? { marker, seriesName, text: `${round(val, 2)} ${unit}` }
                                                : null
                                        })
                                        .filter(Boolean)
                                        .map(({ marker, seriesName, text }) => `${marker} ${seriesName} : ${text}`)
                                    return [header, ...rows].join('<br/>')
                                },
                            },
                        }}
                        toolsOptions={{
                            withLegend: true,
                            withToolLog: true,
                            withToolThreshold: true,
                            withToolMarker: true,
                            withToolLine: true,
                            withToolTypeLine: true,
                            withToolTypeBar: true,
                            withToolQuantification: true,
                            withToolLegend: true,
                        }}
                        withYZoomModal
                        withToolFullScreen
                        withDataZoom
                        thresholds={{
                            list: thresholds,
                            stationType: isPiezo ? STATION_TYPE_NAME.piezometry : STATION_TYPE_NAME.hydrometry,
                            displayCote: isPiezo ?
                                (displayNGF ? MEASURE_COTE.NGF : MEASURE_COTE.DEPTH)
                                :
                                (displayNGF ? HYDRO_MEASURE_COTE.NGF : HYDRO_MEASURE_COTE.HEIGHT),
                            lastLandmark: selectedStation.lastLandmark,
                            groundRefAlti: selectedStation.groundRefAlti,
                            unit: dataType?.unit,
                        }}
                        onEvents={{
                            legendselectchanged: legend => setSelectedLegend(legend.selected),
                        }}
                        style={{ height: chartHeight }}
                        exportDatas={{
                            fileName: i18n.resourceTrackingAndForecasting,
                            headers: exportHeaders,
                            datas: exportRows,
                        }}
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <SummaryIndicator
            title={i18n.resourceTrackingAndForecasting}
            bgColor='white'
            content={getContent()}
        />
    )
}

FollowAndPredictionResourceChart.propTypes = {
    selectedStation: linkStationDetailsShape,
}

export default FollowAndPredictionResourceChart